import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';


const Form8974Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8974: Calculate R&D Tax Credit for Payroll Taxes"
          description="Form 8974 helps small businesses claim R&D tax credits to reduce payroll taxes. Learn about eligibility, 2023 changes, and how to file it with Form 941"
          keywords=""
          canonical="resources/employment-tax/form-8974-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 5,
            mb: 5,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 8974</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
         <Container maxWidth="lg" sx={{ mb: 0 }}>
          {process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' && (
            <DynamicBreadcrumbs />
          )}
          <Grid container spacing={0} rowSpacing={0} m={0} sx={{flexDirection:'row-reverse'}}>
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                 <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
borderLeft:'5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
		    
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-8974-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8974 at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        {'Form 8974 is used to calculate the '}
                        <b>
                          qualified small business payroll tax credit for
                          increasing research activities
                        </b>
                        {'. It is attached to '}
                        <b>Form 941, Form 943, or Form 944</b>
                        {
                          ' when an eligible small business elects to claim the credit against payroll taxes.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>Who Needs to File:</b>
                        {
                          ' Businesses that elected to claim the qualified small business payroll tax credit on an original, timely filed income tax return using '
                        }
                        <b>Form 6765</b>
                        {' and want to apply the credit against payroll taxes.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>What Form 8974 Calculates:</b>
                        {
                          " The form determines the amount of available credit that can be applied against the employer's share of "
                        }
                        <b>Social Security and Medicare taxes</b>
                        {' for the current tax period.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="4"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>Carryforward:</b>
                        {
                          ' Any unused credit may be carried forward to future quarters or years, subject to the applicable rules and limitations.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="5"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <b>Important:</b>
                        {
                          ' Form 8974 cannot be used to claim the payroll tax credit unless the business first elected the credit on '
                        }
                        <b>Form 6765</b>
                        {' with its original, '}
                        timely
                        {' filed income tax return.'}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This form 8974, which can be used on Forms 941, 943, or 944,
                  tells you how much of a qualified small business payroll tax
                  credit you can get for increasing research activities.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Needs to file Form 8974?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }} id="stepby">
                  You must file Form 8974 and attach it to{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/employment-tax/form-943-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    943
                  </Link>
                  , or{' '}
                  <Link
                    href="/resources/employment-tax/form-944-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    944
                  </Link>{' '}
                  if you elected on your income tax return to claim the
                  qualified small business payroll tax credit for increasing
                  research activities against your payroll taxes. This payroll
                  tax credit must be elected on an original, timely filed income
                  tax return (including extensions) using Form 6765. If you
                  haven’t filed an income tax return making the election to
                  claim the payroll tax credit on Form 6765, you cannot file
                  Form 8974 or claim the qualified small business payroll tax
                  credit for increasing research activities on Form 941, 943, or
                  944.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 8974 Instructions - Step-by-Step Guide
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-8974-instructions-img.webp" />
                  <source srcSet="/assets/images/form-8974-instructions-img.png" />
                  <img
                    src="/assets/images/form-8974-instructions-img.png"
                    loading="lazy"
                    alt="Form 8974: Enter EIN, Name, Credit Form, calendar year and quarter."
                    width={644}
                    height={164}
                    className="proportional-img"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Business Information
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Enter Employer Identification Number (EIN):</b> Input
                    your EIN and business name as shown on Form 941, 943, or
                    944.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Form Selection:</b> Check the applicable form box.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Schedule R:</b> If filing Schedule R with Form 941 or
                    943, include the client&apos;s or customer&apos;s EIN.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Calendar Year and Quarter
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Calendar Year:</b> Enter the year as on Form 941, 943, or
                    944.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Quarter:</b> Select the appropriate quarter on Form 941.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 1: Income Tax Return Details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 1–5
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-8974-income-tax-return-img.webp" />
                  <source srcSet="/assets/images/form-8974-income-tax-return-img.png" />
                  <img
                    src="/assets/images/form-8974-income-tax-return-img.png"
                    loading="lazy"
                    alt="Form 8974 Part 1: Lines 1 to 6 showing income tax return details, EIN allocation, credits taken, and remaining credit"
                    width={656}
                    height={224}
                    className="proportional-img"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <ul style={{ marginBottom: 16, marginTop: 0 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (a):</b> Income tax period ending date
                    (MM/DD/YYYY).
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (b):</b> Type of income tax return filed that
                    included Form 6765.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (c):</b> Date the return was filed (MM/DD/YYYY).
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (d):</b> If the EIN on Form 6765 is different from
                    the EIN on Form 8974, enter the EIN from Form 6765.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (e):</b> Credit amount from Form 6765, line 44 (up
                    to $250,000 per year before 2023; $500,000 after).
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (f):</b> Amount of credit previously taken.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Column (g):</b> Remaining credit (subtract column (f)
                    from column (e)). Sum all amounts in column (g) and enter on
                    line 6, column (g).
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: -1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Carryforward and Additional Years
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Carryforward:</b> If credit is unused in the first
                    quarter, carry forward the amount to future quarters or
                    years.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Additional Years:</b> For additional years, use extra
                    rows with the earliest year on line 1. The total may exceed
                    $500,000 if carrying forward unused credits from multiple
                    years.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 2: Determine Usable Credit This Period
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 7–10
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-8974-determine-usable-credit-img.webp" />
                  <source srcSet="/assets/images/form-8974-determine-usable-credit-img.png" />
                  <img
                    src="/assets/images/form-8974-determine-usable-credit-img.png"
                    loading="lazy"
                    alt="Form 8974 Part 2: Lines 7 to 10 shows calculation of available credit for the current tax period."
                    width={660}
                    height={130}
                    className="proportional-img"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>

                <ul style={{ marginBottom: 30, marginTop: 0 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 7:</b> Remaining credit from Part 1, line 6, column
                    (g).
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 8:</b> Social Security tax amount from Form 941,
                    line 5A, column 2.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 9:</b> Additional social security tax from Form 941,
                    line 5B, column 2.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 10:</b> Total of lines 8 and 9.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 11–13
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-8974-line11-13img.webp" />
                  <source srcSet="/assets/images/form-8974-line11-13img.png" />
                  <img
                    src="/assets/images/form-8974-line11-13img.png"
                    loading="lazy"
                    alt="Form 8974 Part 2: calculations for Lines 11–13 with percentage and subtraction steps."
                    width={657}
                    height={120}
                    className="proportional-img"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <ul style={{ marginBottom: 30, marginTop: 0 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 11:</b> Multiply line 10 by 50% (limit credit to
                    employer’s share of social security tax).
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 12:</b> Smaller value of line 7 or line 11 (up to
                    $250,000).
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 13:</b> Subtract line 12 from line 7.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 14–15
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-8974-line14-15-img.webp" />
                  <source srcSet="/assets/images/form-8974-line14-15-img.png" />
                  <img
                    src="/assets/images/form-8974-line14-15-img.png"
                    loading="lazy"
                    alt="Form 8974 Part 2: Lines 14 and 15 showing calculations for Medicare tax amount from Form 941."
                    width={492}
                    height={79}
                    className="proportional-img"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <ul style={{ marginBottom: 30, marginTop: 0 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 14:</b> Medicare tax amount from Form 941, line 5C,
                    column 2.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 15:</b> Multiply line 10 by 50% (limit credit to
                    employer&apos;s share of Medicare tax).
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Lines 16–17
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-8974-line16-17-img.webp" />
                  <source srcSet="/assets/images/form-8974-line16-17-img.png" />
                  <img
                    src="/assets/images/form-8974-line16-17-img.png"
                    loading="lazy"
                    alt="Form 8974 Part 2: Lines 16 and 17 showing credit against the employer share of Medicare tax and total credit."
                    width={654}
                    height={57}
                    className="proportional-img"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <ul style={{ marginBottom: 30, marginTop: 0 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Line 16:</b> Smaller value of line 13 or line 15 (up to
                    $250,000).
                  </li>
                  <li style={{ marginBottom: 10 }} id="claim">
                    <b>Line 17:</b> Add lines 12 and 16. Enter the total on Form
                    941, line 11 (or Form 943, line 12, or Form 944, line 8).
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Important Updates to the 2023 Form 8974
                </Typography>

                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 4 }}>
                  <li>
                    Form 8974 has been updated following the Inflation Reduction
                    Act of 2022, which doubled the available credit from
                    $250,000 to $500,000.
                  </li>
                  <li>
                    The employer share of social security tax credit is now
                    reduced to $250,000, with any remaining credit applied to
                    Medicare taxes.{' '}
                  </li>
                  <li>
                    New lines 13-17 have been added for the 2023 tax year.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Claiming the R&D Payroll Tax Credit
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The following steps must be taken by qualified small
                  businesses to claim the Research and Development (R&D) Payroll
                  Tax Credit:
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Choose the Credit on Form 6765:</b> Choosing the R&D
                    payroll tax credit is the first thing you need to do. You
                    file Form 6765 with your income tax return. With this
                    choice, small businesses can use the credit for payroll
                    taxes instead of income taxes.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>File Form 8974:</b> Once you&apos;ve chosen the credit,
                    you must complete Form 8974 and attach it to your payroll
                    tax forms (Forms 941, 943, or 944) to figure out and claim
                    the credit against the employer&apos;s share of Social
                    Security taxes.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Send with Payroll Forms:</b> You send Form 8974 with your
                    quarterly or yearly payroll tax forms, like{' '}
                    <Link
                      href="/resources/employment-tax/form-941-instructions/"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 941
                    </Link>
                    . With this credit, you can pay up to $250,000 less in
                    payroll taxes each year.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  E-file Form 941 and Attach Form 8974 with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps to Complete{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 941
                  </Link>{' '}
                  & Form 8974 Easily Using TaxZerone:
                </Typography>
                <Box component="ol">
                  <li style={{ marginBottom: 8 }}>
                    Select the tax year and quarter.
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Enter the details for Form 941.
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Enter the qualified small business payroll tax credit on
                    Form 941, Line 11.
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Enter the Form 8974 information and ensure that Line 17
                    matches Form 941, Line 11.
                  </li>
                  <li>Review and submit Form 941 to the IRS.</li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Start using TaxZerone today for fast, efficient, <br />
              and accurate Form 941 filing.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Experience hassle-free e-filing with our user-friendly platform,
              ensuring compliance <br />
              and peace of mind every step of the way.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8974instructions`}
              >
                E-file Form 941 Today
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8974Instructions;
